/**
 * Utility functions for handling hierarchical selection logic
 */

export interface RowData {
  id: any;
  parentReference?: {
    id: any;
  };
  parentId?: string | null; // Add support for DocumentData structure
}

/**
 * Get parent ID from row data, supporting both parentReference and parentId structures
 */
export const getParentId = (row: RowData): any => {
  return row.parentReference?.id || row.parentId;
};

/**
 * Get all descendant IDs for a given parent ID
 */
export const getAllDescendants = (parentId: any, allRows: RowData[]): any[] => {
  if (!allRows) return [];
  const children = allRows.filter((row) => getParentId(row) === parentId);
  return children.reduce((acc, child) => {
    return [...acc, child.id, ...getAllDescendants(child.id, allRows)];
  }, [] as any[]);
};

/**
 * Get all ancestor IDs for a given child ID
 */
export const getAllAncestors = (childId: any, allRows: RowData[]): any[] => {
  if (!allRows) return [];
  const child = allRows.find((row) => row.id === childId);
  if (!child) return [];
  const parentId = getParentId(child);
  if (!parentId) return [];
  return [parentId, ...getAllAncestors(parentId, allRows)];
};

/**
 * Check if a row is effectively selected based on selection state and inheritance
 */
export const isRowEffectivelySelected = (
  rowId: any,
  selectedRowIds: Set<any>,
  explicitlyDeselectedIds: Set<any>,
  allRows: RowData[]
): boolean => {
  // Row is explicitly deselected
  if (explicitlyDeselectedIds.has(rowId)) return false;

  // Row is directly selected
  if (selectedRowIds.has(rowId)) return true;

  // Check if any ancestor is selected (and this row is not explicitly deselected)
  const ancestors = getAllAncestors(rowId, allRows);
  return ancestors.some(
    (ancestorId) => selectedRowIds.has(ancestorId) && !explicitlyDeselectedIds.has(rowId)
  );
};

/**
 * Get the selection state of children for a given parent (recursively considers all descendants)
 */
export const getChildrenSelectionState = (
  parentId: any,
  allRows: RowData[],
  selectedRowIds: Set<any>,
  explicitlyDeselectedIds: Set<any>
) => {
  if (!allRows) return { allSelected: false, someSelected: false, noneSelected: true };

  const children = allRows.filter((child) => getParentId(child) === parentId);
  if (children.length === 0) return { allSelected: false, someSelected: false, noneSelected: true };

  let totalDescendants = 0;
  let selectedDescendants = 0;

  // Recursively count all descendants and their selection state
  const countDescendantsRecursively = (nodeId: any) => {
    const directChildren = allRows.filter((child) => getParentId(child) === nodeId);

    for (const child of directChildren) {
      totalDescendants++;

      const isSelected = isRowEffectivelySelected(
        child.id,
        selectedRowIds,
        explicitlyDeselectedIds,
        allRows
      );

      if (isSelected) {
        selectedDescendants++;
      }

      // Recursively count this child's descendants
      countDescendantsRecursively(child.id);
    }
  };

  countDescendantsRecursively(parentId);

  return {
    allSelected: totalDescendants > 0 && selectedDescendants === totalDescendants,
    someSelected: selectedDescendants > 0,
    noneSelected: selectedDescendants === 0,
  };
};

/**
 * Calculate header checkbox state based on visible rows
 */
export const getHeaderCheckboxState = (
  rows: RowData[],
  selectedRowIds: Set<any>,
  explicitlyDeselectedIds: Set<any>,
  allRows: RowData[]
) => {
  if (!rows || rows.length === 0) {
    return { checked: false, indeterminate: false };
  }

  const selectedVisibleRows = rows.filter((row) =>
    isRowEffectivelySelected(row.id, selectedRowIds, explicitlyDeselectedIds, allRows)
  );

  // Check if any visible row has descendants selected (even if not visible)
  const hasIndeterminateRows = rows.some((row) => {
    if (isRowEffectivelySelected(row.id, selectedRowIds, explicitlyDeselectedIds, allRows))
      return false; // Already selected

    // Check if this row has any selected descendants in allRows
    const descendants = getAllDescendants(row.id, allRows);
    return descendants.some((descendantId) =>
      isRowEffectivelySelected(descendantId, selectedRowIds, explicitlyDeselectedIds, allRows)
    );
  });

  const totalAffectedRows = selectedVisibleRows.length + (hasIndeterminateRows ? 1 : 0);

  if (totalAffectedRows === 0) {
    return { checked: false, indeterminate: false };
  } else if (selectedVisibleRows.length === rows.length && !hasIndeterminateRows) {
    return { checked: true, indeterminate: false };
  } else {
    return { checked: false, indeterminate: true };
  }
};
